import {useMemo, useState} from "react";
import {DVIREntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UseDVIRDataTableFilterProps = Omit<UseDataTableFilterProps<DVIREntity>, 'filterConfig'>

export function useDVIRDataTableFilter(props: UseDVIRDataTableFilterProps): UseDataTableFilterResult<DVIREntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [personQuery, setPersonQuery] = useState<string | undefined>(undefined);
  const [personDebouncedQuery] = useDebouncedValue(personQuery, 200);

  const [vehicleQuery, setVehicleQuery] = useState<string | undefined>(undefined);
  const [vehicleDebouncedQuery] = useDebouncedValue(vehicleQuery, 200);

  const [trailerQuery, setTrailerQuery] = useState<string | undefined>(undefined);
  const [trailerDebouncedQuery] = useDebouncedValue(trailerQuery, 200);

  const [statusQuery, setStatusQuery] = useState<string | undefined>(undefined);
  const [statusDebouncedQuery] = useDebouncedValue(statusQuery, 200);

  const filterConfig: DataTableFilterConfig<DVIREntity> = useMemo(() => {
    return {
      "personName": {
        setQuery: setPersonQuery,
        query: personQuery,
        debouncedQuery: personDebouncedQuery,
        filter: ({personName}, query) => !personName || !personName.toLowerCase().includes(query.toLowerCase())
      },
      "vehicleName": {
        setQuery: setVehicleQuery,
        query: vehicleQuery,
        debouncedQuery: vehicleDebouncedQuery,
        filter: ({vehicleName}, query) => !vehicleName || !vehicleName.toLowerCase().includes(query.toLowerCase())
      },
      "trailerName": {
        setQuery: setTrailerQuery,
        query: trailerQuery,
        debouncedQuery: trailerDebouncedQuery,
        filter: ({trailerName}, query) => !trailerName || !trailerName.toLowerCase().includes(query.toLowerCase())
      },
      "status": {
        setQuery: setStatusQuery,
        query: statusQuery,
        debouncedQuery: statusDebouncedQuery,
        filter: ({status}, query) => !status || !status.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    personQuery,
    personDebouncedQuery,
    vehicleQuery,
    vehicleDebouncedQuery,
    trailerQuery,
    trailerDebouncedQuery,
    statusQuery,
    statusDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
